import type { Quiz } from "./types";

/**
 * The self-assessment the whole site is really about, in the one form people
 * will actually complete.
 *
 * "Am I addicted to my phone" is asked constantly and answered badly: the
 * results are either a joke or a diagnosis, and neither is true. This one is
 * deliberately about ATTENTION rather than hours, because hours are the number
 * everyone already has and nobody has ever been changed by.
 */
export const article: Quiz = {
  slug: "phone-and-faith-test",
  kind: "reflection",
  title: "Phone and faith test: where is your attention going?",
  h1: "The phone and faith test",
  description:
    "Twelve honest questions about your phone, your evenings and your prayer life. No screen-time number required, and no diagnosis at the end — just what your answers point at.",
  targets: [
    "phone addiction test christian",
    "am i addicted to my phone quiz",
    "christian phone habits self assessment",
    "is my phone hurting my prayer life",
  ],
  published: "2026-08-26",
  standfirst:
    "Twelve questions about the small moments — the first thing you reach for, the thing you do while the kettle boils, the reason you unlocked it just now.",
  takeaways: [
    "Screen-time totals are the wrong measure: two people with identical five-hour averages can have completely different problems, because what matters is when the phone interrupts and what it interrupts.",
    "The four patterns this test separates are the quiet drift, the evening slide, the tether and the loop — they need different responses, and treating one as another is why most attempts to cut back fail.",
    "Nothing here is a diagnosis. Compulsive phone use overlaps with anxiety, low mood and loneliness, and a quiz cannot tell those apart from a habit.",
    "The most useful answer is usually the one about the first sixty seconds after waking, because that single moment sets the shape of the whole day's attention.",
  ],
  intro: [
    {
      type: "p",
      text: "Nearly every phone-habit test asks you for a number. How many hours yesterday, how many pickups, how many notifications. The number is easy to collect and almost useless: two people with the same five-hour average can be living completely different lives, because one of them spent it on maps, messages and a long call with their mother, and the other spent it in eleven-minute pieces scattered through every gap in the day, including the gaps that were supposed to be prayer.",
    },
    {
      type: "p",
      text: "So this asks about the gaps instead. Where the phone appears, what it interrupts, and what happens in the sixty seconds after you wake up. Twelve questions, and the result updates as you go — you do not have to reach the end to see what your answers are pointing at. If you would rather read the argument first, {why-screen-time-limits-stop-working} covers why the number on the settings screen is not the thing to fix.",
      links: {
        "why-screen-time-limits-stop-working":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "note",
      text: "This is not a diagnosis and nothing here is a clinical instrument. If your phone use is tangled up with anxiety, sleeplessness or low mood, that is worth saying out loud to a doctor or a pastor rather than to a web page.",
    },
  ],
  questions: [
    {
      id: "wake",
      prompt: "The first sixty seconds after you wake up — what happens?",
      options: [
        {
          text: "The phone is in my hand before my feet are on the floor.",
          scores: { loop: 3, tether: 2 },
        },
        {
          text: "I check the time, then somehow I have read four things.",
          scores: { tether: 3, slide: 1 },
        },
        {
          text: "I look at it, but I put it down and get up.",
          scores: { drift: 2 },
        },
        {
          text: "It is in another room and I do not see it until I am dressed.",
          scores: { drift: 1 },
        },
      ],
    },
    {
      id: "kettle",
      prompt: "The kettle is boiling. Two minutes with nothing to do. You…",
      options: [
        { text: "unlock the phone without deciding to.", scores: { loop: 3 } },
        {
          text: "unlock it, decide it was pointless, keep going anyway.",
          scores: { loop: 2, tether: 1 },
        },
        {
          text: "check one specific thing and put it down.",
          scores: { tether: 1, drift: 1 },
        },
        { text: "stand there and wait for the kettle.", scores: { drift: 1 } },
      ],
    },
    {
      id: "prayer",
      prompt: "You sit down to pray. How long before the phone enters it?",
      options: [
        {
          text: "It is already there — I use it for the reading, and then I am elsewhere.",
          scores: { tether: 3, loop: 1 },
        },
        {
          text: "A few minutes in, I remember something I need to look up.",
          scores: { tether: 2, drift: 1 },
        },
        {
          text: "It buzzes and I decide whether to look. Sometimes I look.",
          scores: { drift: 2, slide: 1 },
        },
        {
          text: "It is face down in another room and this is not a question I have.",
          scores: { drift: 1 },
        },
      ],
    },
    {
      id: "night",
      prompt: "What time do you actually put it down at night?",
      options: [
        {
          text: "Later than I planned, most nights, and I could not tell you when.",
          scores: { slide: 3, loop: 2 },
        },
        {
          text: "Within half an hour of the time I meant to.",
          scores: { slide: 2 },
        },
        {
          text: "It goes on the charger at a fixed time and mostly stays there.",
          scores: { drift: 1 },
        },
        {
          text: "It does not come into the bedroom.",
          scores: { drift: 1 },
        },
      ],
    },
    {
      id: "reason",
      prompt: "The last time you unlocked it, what were you looking for?",
      options: [
        { text: "I genuinely do not know.", scores: { loop: 3, slide: 1 } },
        {
          text: "Something specific, and then twenty minutes of something else.",
          scores: { loop: 2, tether: 2 },
        },
        { text: "Something specific, which I did.", scores: { drift: 1 } },
        {
          text: "A person — a message, a call, a photo to send.",
          scores: { drift: 2 },
        },
      ],
    },
    {
      id: "waiting",
      prompt: "You are waiting for someone who is ten minutes late.",
      options: [
        {
          text: "The phone is out before I have sat down.",
          scores: { loop: 2, tether: 2 },
        },
        {
          text: "I last about ninety seconds.",
          scores: { tether: 2, slide: 1 },
        },
        {
          text: "I get it out, then remember I like waiting, and put it away.",
          scores: { drift: 2 },
        },
        { text: "I just wait. It is ten minutes.", scores: { drift: 2 } },
      ],
    },
    {
      id: "conversation",
      prompt: "Mid-conversation, someone mentions something you could look up.",
      options: [
        {
          text: "I look it up, and then I am gone for a while.",
          scores: { loop: 2, tether: 1 },
        },
        {
          text: "I look it up, read it out, put it away.",
          scores: { tether: 1, drift: 1 },
        },
        {
          text: "I say I will look it up later, and forget.",
          scores: { drift: 2 },
        },
        {
          text: "I leave it. The conversation is the thing.",
          scores: { drift: 2 },
        },
      ],
    },
    {
      id: "notification",
      prompt: "A notification arrives while you are working on something hard.",
      options: [
        {
          text: "I read it immediately, every time.",
          scores: { tether: 3, loop: 1 },
        },
        {
          text: "I do not read it, but I stop working until I have.",
          scores: { tether: 2, slide: 1 },
        },
        {
          text: "I notice it and carry on, mostly.",
          scores: { drift: 1, slide: 1 },
        },
        {
          text: "Notifications are off for anything that is not a human being.",
          scores: { drift: 2 },
        },
      ],
    },
    {
      id: "bible",
      prompt: "When did you last read scripture for longer than five minutes?",
      options: [
        {
          text: "I cannot remember, and I own three Bibles.",
          scores: { slide: 2, loop: 2 },
        },
        {
          text: "Sunday. I mean to in the week and the week goes.",
          scores: { slide: 2, tether: 1 },
        },
        { text: "In the last few days.", scores: { drift: 1 } },
        { text: "Today, and most days.", scores: { drift: 2 } },
      ],
    },
    {
      id: "sunday",
      prompt: "During the sermon on Sunday, where is your phone?",
      options: [
        {
          text: "In my hand, and not only for the passage.",
          scores: { tether: 3, loop: 1 },
        },
        {
          text: "In my hand for the passage, and I check it once or twice.",
          scores: { tether: 2 },
        },
        {
          text: "In my pocket, buzzing occasionally.",
          scores: { drift: 1, slide: 1 },
        },
        { text: "Silent, away, not a factor.", scores: { drift: 2 } },
      ],
    },
    {
      id: "feeling",
      prompt: "Which of these is nearest to true?",
      options: [
        {
          text: "I pick it up when I feel something I would rather not feel.",
          scores: { loop: 3, slide: 1 },
        },
        {
          text: "I pick it up when I am bored, which is more often than I would admit.",
          scores: { loop: 2, drift: 1 },
        },
        {
          text: "I pick it up because something needs doing on it.",
          scores: { tether: 2 },
        },
        {
          text: "I pick it up at particular times and it stays down between them.",
          scores: { drift: 2 },
        },
      ],
    },
    {
      id: "attempt",
      prompt: "You have tried to cut back before. What happened?",
      options: [
        {
          text: "I set a limit, tapped 'ignore', and never thought about it again.",
          scores: { slide: 3, loop: 1 },
        },
        {
          text: "It worked for four days and then quietly stopped.",
          scores: { slide: 2, tether: 1 },
        },
        {
          text: "It worked for the apps I blocked; I moved to different ones.",
          scores: { loop: 2, tether: 1 },
        },
        {
          text: "I have not really tried, because I am not sure it is a problem.",
          scores: { drift: 2 },
        },
      ],
    },
  ],
  outcomes: [
    {
      id: "drift",
      name: "The quiet drift",
      summary:
        "Your phone is not running your day. It is taking small pieces of it — and the pieces it takes are the quiet ones, which are the pieces prayer used to live in.",
      body: [
        "Nothing in your answers looks like compulsion. You put it down, you wait for kettles, you can be ten minutes early somewhere without needing a screen. That is genuinely rarer than it sounds, and it is worth naming rather than skipping past on the way to a problem.",
        "What your answers do show is erosion at the edges. The gaps that used to be dead time — the walk, the queue, the two minutes before the meeting starts — have quietly filled up. Those gaps are not wasted time; they are where most people's thinking happens, and historically they are where a great deal of praying happened too. Losing them is not dramatic and does not show up in a screen-time report, which is exactly why it goes unnoticed for years.",
        "The response here is not restriction. There is nothing to restrict. It is deciding what one or two of those gaps are for, and defending them the way you would defend an appointment. That is a smaller-sounding job than cutting back and a harder one, because nothing external will ever remind you — an empty ten minutes does not announce itself, and the drift only shows up as a vague sense that there is less room in the week than there used to be.",
      ],
      verse: {
        text: "Be still, and know that I am God.",
        ref: "Psalm 46:10",
      },
      practice: [
        "Pick one recurring gap — the commute, the walk to the car, the first ten minutes of lunch — and give it to prayer rather than to nothing in particular.",
        "Turn off notifications for everything that is not a person. You will lose nothing you can name a week later.",
        "Leave the phone in another room for one evening this week, and notice what you do instead. That is the data, not the screen-time number.",
      ],
    },
    {
      id: "slide",
      name: "The evening slide",
      summary:
        "Your days are mostly fine. Your nights are where the hours go, and they go later than you decided they would.",
      body: [
        "The pattern in your answers is time-shaped rather than compulsion-shaped: you are not reaching for the phone all day, you are failing to put it down at the one moment you meant to. That single failure is expensive out of proportion to its size, because it takes the two things that most reliably hold a spiritual life together — sleep, and the first hour of the next morning.",
        "It is also the pattern that responds best to a change in the environment rather than a change in resolve. Nobody slides at eleven at night because they have weak convictions; they slide because at eleven at night the decision is being made by a tired person, and the tired person is not the one who set the goal. Every attempt that relies on that person choosing well is an attempt that will work until the first bad Tuesday.",
        "Which is what your last answer describes: a limit you dismissed and stopped seeing. The fix is not a firmer limit. It is a limit that costs something to get past — a few seconds of friction with a real reason attached, arriving before the slide starts rather than an hour into it.",
      ],
      verse: {
        text: "In peace I will both lay myself down and sleep, for you alone, Yahweh, make me live in safety.",
        ref: "Psalm 4:8",
      },
      practice: [
        "Set the block for thirty minutes before the time you actually go to bed, not the time you tell people you go to bed.",
        "Charge the phone outside the bedroom for seven nights. Buy a clock; it costs less than one month of anything.",
        "Decide in advance what the last thing of the day is — a psalm, a page, a short prayer — so the evening ends on something rather than trailing off.",
      ],
    },
    {
      id: "tether",
      name: "The tether",
      summary:
        "You are not scrolling aimlessly. You are permanently reachable — and something that can interrupt you at any moment is something you are never fully away from.",
      body: [
        "Your answers describe a person who mostly uses the phone for real reasons: messages, lookups, the passage in church. The problem is not what you open, it is that you are never closed. Every notification is read on arrival, work follows you into the evening, and the phone is already in your hand at the start of prayer, which means prayer begins one tap away from everything else.",
        "This is the pattern people defend hardest, because each individual instance is justifiable. And each one is. The cost is not in any instance; it is that attention which can be claimed at any moment is attention that never fully settles anywhere — you are always partly braced. Long, unbroken concentration is the raw material of both deep work and unhurried prayer, and this pattern quietly removes the possibility of it.",
        "The thing to change is availability, not usage. You do not need to use the phone less. You need there to be periods when it cannot reach you, and for the people who matter to know when those are. Announced in advance, an unreachable hour costs almost nothing; discovered by accident, it costs something small every time.",
      ],
      verse: {
        text: "No one can serve two masters, for either he will hate the one and love the other, or else he will be devoted to one and despise the other.",
        ref: "Matthew 6:24",
      },
      practice: [
        "Use a paper Bible, or the app's reader with everything else blocked, for your reading — so the passage is not sharing a screen with your inbox.",
        "Choose two windows a day when nothing can reach you, and tell the two people who would worry.",
        "Move work messaging off the phone entirely for one week and see what actually breaks. It is usually less than you fear.",
      ],
    },
    {
      id: "loop",
      name: "The loop",
      summary:
        "You are reaching for it before you decide to, and often you cannot say what you were looking for. That is not weakness — it is a loop doing exactly what it was designed to do.",
      body: [
        "Several of your answers point at the same thing: the phone arrives in your hand ahead of any intention, and it arrives most reliably in the moments when you feel something uncomfortable. Boredom, dread of a task, the low hum at the end of a hard day. The unlock is not a choice you are losing; by the time there is anything to choose, it has already happened.",
        "That is worth saying plainly because self-blame is the least useful response available here, and it is the usual one. These products are built by people whose job is to make the loop close faster, and they are extremely good at it. Willpower is being asked to win a fight it is not equipped for, and losing it is not a character finding.",
        "What breaks a loop is not resolve but a gap — something between the reach and the reward, arriving automatically, so that the moment stops being invisible. It does not have to be a long gap. It has to be reliable, and it has to require something of you, because a barrier you can dismiss without thinking is one you stop seeing within a week.",
        "It is also worth checking what sits underneath. Compulsive use overlaps heavily with anxiety, low mood and loneliness, and if the reaching is mostly toward escape, the phone is the symptom rather than the thing. That is not a reason to leave the habit alone, because the habit is real and worth interrupting on its own terms. It is a reason not to expect any app to finish the job.",
      ],
      verse: {
        text: "Like a city that is broken down and without walls is a man whose spirit is without restraint.",
        ref: "Proverbs 25:28",
      },
      practice: [
        "Put a real barrier on the two or three apps you reach for without deciding — one that takes a moment to get past rather than a tap.",
        "For one week, write down the feeling in the moment before you unlock. Three words is enough. The pattern will be obvious by day four.",
        "Tell one person. A loop that somebody else knows about behaves differently from one that nobody does.",
      ],
    },
  ],
  after: [
    {
      type: "h2",
      id: "what-this-measures",
      text: "What these four patterns actually are",
    },
    {
      type: "p",
      text: "They are not severities. The loop is not a worse version of the drift; they are different problems with different fixes, and applying the wrong fix is the usual reason an attempt to cut back fails. Somebody with an evening slide who installs an aggressive all-day blocker will resent it by Wednesday, because the daytime was never the problem. Somebody in a loop who sets a gentle nightly reminder will dismiss it without reading it, because a reminder is not a barrier.",
    },
    {
      type: "ul",
      items: [
        "The quiet drift is a question of what the gaps are for. Nothing needs blocking; something needs claiming.",
        "The evening slide is a question of when. One window, defended properly, does most of the work.",
        "The tether is a question of availability. Fewer interruptions, not less usage.",
        "The loop is a question of friction. Something has to sit between the reach and the reward.",
      ],
    },
    {
      type: "h2",
      id: "why-a-prayer",
      text: "Why the barrier is a prayer",
    },
    {
      type: "p",
      text: "Bible Shield is built on the middle of that last one. When a blocked app is opened, it asks for a short prayer before it opens — thirty seconds, written for the day you are actually having, not a countdown to stare through. The gap is the mechanism, and what fills the gap is the difference between a timer, which teaches you to wait, and a prayer, which turns the reach itself into the reminder. {The-full-argument-for-that} is a longer piece.",
      links: {
        "The-full-argument-for-that": "/blog/christian-app-blocker-guide",
      },
    },
    {
      type: "note",
      text: "The result of this test is a description, not a verdict. Take it again in a month; the interesting thing is not which one you got but whether it moved.",
    },
  ],
  faq: [
    {
      q: "Is this a real psychological test?",
      a: "No. It is a structured way to look at your own habits, written by the people who build a phone-blocking app, and it has not been validated against anything. Genuine screening instruments exist and are administered by clinicians. If your phone use is causing real distress, that is the route.",
    },
    {
      q: "Why does it not ask for my screen time?",
      a: "Because the number does not distinguish between the two situations that need completely different responses: four hours of messages, maps and photos, and four hours in eleven-minute fragments scattered through every gap in the day. When the phone appears matters far more than how long it stays.",
    },
    {
      q: "Can I get a different result?",
      a: "Yes — the result is the sum of the answers you have given so far, and it updates as you change them. Nothing is stored anywhere, and the page does not send your answers to us or to anyone else.",
    },
    {
      q: "I got the loop. Is that bad?",
      a: "It is the pattern with the most machinery behind it, so it is where willpower alone is least likely to work and where a small automatic barrier does the most. It is not a moral finding. Reaching for a phone before deciding to is what owning a phone built to a retention target feels like.",
    },
  ],
  related: [
    "screen-time-limits-dont-work",
    "how-to-stop-scrolling-at-night",
    "christian-app-blocker-guide",
  ],
  relatedQuizzes: ["prayer-style-quiz", "which-bible-character-are-you"],
};
